import { useEffect, useMemo, useState } from 'react';

import {
    echoedOutreachWindow,
    outlierFeedCap,
    OUTREACH_REFRESH_INTERVAL_MS,
    outreachBucketMs,
    type OutreachTabProps,
    outreachWindowSummary,
} from '@apps/web/components/admin/outreach_observability/constants';
import { InFlightBoard } from '@apps/web/components/admin/outreach_observability/in_flight_board';
import { OutlierFeed } from '@apps/web/components/admin/outreach_observability/outlier_feed';
import {
    formatOutlierBucketRange,
    OutlierTicker,
} from '@apps/web/components/admin/outreach_observability/outlier_ticker';
import { PulseStrip } from '@apps/web/components/admin/outreach_observability/pulse_strip';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { useAdminOutreachObservability } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OutreachOutlierRun } from '@libraries/typescript/api_client';

function filterOutliers(
    outliers: Array<OutreachOutlierRun>,
    selectedBucketStart: string | null,
    bucketMs: number,
): Array<OutreachOutlierRun> {
    if (selectedBucketStart === null) {
        return outliers;
    }
    const startMs = Date.parse(selectedBucketStart);
    const endMs = startMs + bucketMs;
    return outliers.filter((run) => {
        const createdMs = Date.parse(run.created_at);
        return createdMs >= startMs && createdMs < endMs;
    });
}

export function PulseTab({
    organizationIds,
    window: liveWindow,
    urgency,
}: OutreachTabProps) {
    const { data: responseData } = useAdminOutreachObservability(
        {
            sections: ['live', 'kpi_trends', 'comparisons'],
            organizationIds,
            window: liveWindow,
            urgency,
        },
        { refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS },
    );
    const data = responseData?.urgency === urgency ? responseData : undefined;
    const dataWindow = (data ? echoedOutreachWindow(data) : null) ?? liveWindow;
    const bucketMs = outreachBucketMs(dataWindow);
    const feedCap = outlierFeedCap(dataWindow);
    const windowSummary = outreachWindowSummary(dataWindow);

    // Histogram selection: a bucket_start scoping the outlier feed, or null for
    // the whole live window.
    const [selectedBucketStart, setSelectedBucketStart] = useState<
        string | null
    >(null);
    const intraday = useMemo(() => data?.intraday ?? [], [data?.intraday]);
    // Resolve the selection to its histogram bucket. A selection that has
    // scrolled out of the window (before the effect below clears it) resolves
    // to undefined, so all scoped UI falls back to the unscoped window view for
    // that one render instead of mislabeling counts.
    const selectedBucket = selectedBucketStart
        ? intraday.find((bucket) => bucket.bucket_start === selectedBucketStart)
        : undefined;
    const activeBucketStart = selectedBucket?.bucket_start ?? null;
    const feedOutliers = filterOutliers(
        data?.outliers ?? [],
        activeBucketStart,
        bucketMs,
    );
    // The histogram counts outliers uncapped, but the feed keeps a bounded
    // number of the newest runs. For a selected bucket we know its true count,
    // so flag when older runs are truncated from the feed; the unscoped header
    // just reports what the feed shows (its window doesn't line up
    // bucket-for-bucket).
    const truncatedCount = selectedBucket
        ? selectedBucket.outlier_count - feedOutliers.length
        : 0;
    const selectedRangeLabel = selectedBucket
        ? formatOutlierBucketRange(
              Date.parse(selectedBucket.bucket_start),
              bucketMs,
              dataWindow,
          )
        : null;

    // Drop a selection once its bucket scrolls out of the current window.
    useEffect(() => {
        if (
            selectedBucketStart !== null &&
            !intraday.some(
                (bucket) => bucket.bucket_start === selectedBucketStart,
            )
        ) {
            setSelectedBucketStart(null);
        }
    }, [intraday, selectedBucketStart]);

    if (!data) {
        return (
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((index) => (
                        <Skeleton key={index} className="h-24 w-full" />
                    ))}
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-4">
            <PulseStrip
                pulse={data.pulse}
                metricFlags={data.metric_flags}
                windowSummary={windowSummary}
                window={dataWindow}
                kpiHourlyTrends={data.kpi_hourly_trends}
                comparisons={data.comparisons ?? null}
                organizationIds={organizationIds}
                urgency={urgency}
            />
            <InFlightBoard
                organizationIds={organizationIds}
                urgency={urgency}
            />
            <Card className="flex min-h-0 flex-col">
                <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                        <CardTitle>
                            {selectedBucket
                                ? `Outliers · ${selectedRangeLabel} (${selectedBucket.outlier_count})`
                                : `Outliers · ${windowSummary} (${feedOutliers.length})`}
                        </CardTitle>
                        {selectedBucket ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setSelectedBucketStart(null);
                                }}
                            >
                                Clear
                            </Button>
                        ) : null}
                    </div>
                    <CardDescription>
                        Outlier volume by histogram bucket ({windowSummary}) —
                        taller means more. Click a bar to scope the feed to that
                        bucket.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex min-h-0 flex-col gap-4">
                    <OutlierTicker
                        intraday={intraday}
                        selectedBucketStart={activeBucketStart}
                        onSelect={setSelectedBucketStart}
                        ghostBuckets={
                            data.comparisons?.yesterday.intraday_outliers ?? []
                        }
                        window={dataWindow}
                        bucketMs={bucketMs}
                    />
                    <div className="max-h-[26rem] min-h-0 flex-1 overflow-y-auto">
                        <OutlierFeed
                            outliers={feedOutliers}
                            emptyMessage={
                                selectedBucket
                                    ? selectedBucket.outlier_count > 0
                                        ? `No outliers from this bucket are retained among the newest ${feedCap} runs.`
                                        : 'No outlier runs in this selected bucket.'
                                    : `No outlier runs (${windowSummary}).`
                            }
                        />
                        {truncatedCount > 0 && selectedBucket ? (
                            <p className="text-muted-foreground pt-2 text-center text-xs">
                                Showing {feedOutliers.length} of{' '}
                                {selectedBucket.outlier_count} outliers in this
                                bucket — the feed keeps only the newest{' '}
                                {feedCap} runs.
                            </p>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
